/* eslint-disable @typescript-eslint/no-empty-object-type */
import { memo, createContext, useContext, type ReactNode, useMemo } from 'react';
import useAuthContext from '@/features/auth/_shared/contexts/auth.context';
import { useSocket } from '@/websockets/hooks/use-socket';
import { IS_APP_DEVELOPMENT } from '@/configs/app.config';

interface SocketContextType {
  socket: ReturnType<typeof useSocket>;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

// We don't use these types in the provider, but we need to define them to satisfy the useSocket type
// We use the types in the hooks that use the socket
// so that we can use the socket in the hooks
export type TSatisfyServerToClientEvents = {}; // server -> client (listeners)
export type TSatisfyClientToServerEvents = {}; // client -> server (emitters)

interface ISocketContextProviderProps {
  children: ReactNode;
}

const SocketContextProvider = memo(({ children }: ISocketContextProviderProps) => {
  const { token, isAuthenticated, hydrated, state } = useAuthContext();

  const socket = useSocket<TSatisfyServerToClientEvents, TSatisfyClientToServerEvents>({
    token: hydrated && isAuthenticated && !state?.loading ? token : null,
    enabled: hydrated && isAuthenticated && !state?.loading,
    autoReconnect: true,
    isDebug: Boolean(IS_APP_DEVELOPMENT),
  });

  const socketContext = useMemo(() => ({ socket }), [socket]);

  return <SocketContext.Provider value={socketContext}>{children}</SocketContext.Provider>;
});

SocketContextProvider.displayName = 'SocketContextProvider';

export default SocketContextProvider;

/**
 * useSocketContext is a hook that returns the socket context
 * it is used to access the socket from the socket context
 * it is used in the socket event listeners provider
 */
export function useSocketContext() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocketContext must be used inside SocketContextProvider');
  return ctx;
}
